import type { PermissionLevel } from '@datapad/data-model';

/**
 * ID for each sub-app supported by the Datapad.
 */
export enum AppId {
	Profile,
	GalaxyMap,
	Contacts,
	Calendar,
	Timeline,
	Players,
	Campaigns,
}

/**
 * Checks if an app is available for a given permission level.
 *
 * @param appId - The app to check availability for.
 * @param permissionLevel - The user's effective permission level.
 * @returns True if the app is available, false otherwise.
 */
export function isAppAvailable(appId: AppId, permissionLevel: PermissionLevel): boolean {
	switch (appId) {
		case AppId.Profile:
			// Profile is unavailable for guests
			return permissionLevel !== 'guest';
		case AppId.Players:
		case AppId.Campaigns:
			// Admin apps are only available to game masters
			return permissionLevel === 'game-master';
		default:
			// All other apps are always available
			return true;
	}
}

/**
 * Gets the first available app for a given permission level.
 * Used as a fallback when the current app is unavailable.
 *
 * @param permissionLevel - The user's effective permission level.
 * @returns The first available app for the permission level.
 */
export function getFirstAvailableApp(permissionLevel: PermissionLevel): AppId {
	// For guests, default to GalaxyMap since Profile is unavailable
	if (permissionLevel === 'guest') {
		return AppId.GalaxyMap;
	}
	// For players and GMs, default to Profile
	return AppId.Profile;
}
